import type { StyleSpecification } from "maplibre-gl";

/** OpenFreeMap Bright — same URL the app has always used for the vector basemap. */
export const VECTOR_STYLE_URL = "https://tiles.openfreemap.org/styles/bright";

export const BASEMAP_PREF_KEY = "mapus.basemap";

export type BasemapMode = "vector" | "satellite";

export type BasemapStorage = { getItem(key: string): string | null };

export type BasemapWritableStorage = BasemapStorage & {
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

export function readBasemapPref(storage: BasemapStorage): BasemapMode {
  try {
    return storage.getItem(BASEMAP_PREF_KEY) === "satellite" ? "satellite" : "vector";
  } catch {
    return "vector";
  }
}

export function writeBasemapPref(storage: BasemapWritableStorage, mode: BasemapMode): void {
  try {
    if (mode === "vector") storage.removeItem(BASEMAP_PREF_KEY);
    else storage.setItem(BASEMAP_PREF_KEY, mode);
  } catch {
    /* private mode */
  }
}

export function satelliteTileUrl(key: string, z: number, x: number, y: number): string {
  return `https://api.maptiler.com/tiles/satellite-v2/${z}/${x}/${y}.jpg?key=${encodeURIComponent(key)}`;
}

/** Low-zoom tile used to verify the MapTiler key before showing the satellite toggle. */
export function satelliteProbeTileUrl(key: string): string {
  return satelliteTileUrl(key, 0, 0, 0);
}

export const SATELLITE_ATTRIBUTION_TEXT = "© MapTiler © OpenStreetMap contributors";

export const VWORLD_ATTRIBUTION_TEXT = "© 국토교통부 브이월드(VWorld)";

/** Approximate Korea coverage for VWorld satellite (EPSG:3857 bounds: west, south, east, north). */
export const VWORLD_KOREA_BOUNDS: [number, number, number, number] = [124.5, 33.0, 132.0, 38.7];

export const VWORLD_SOURCE_ID = "vworld_satellite";
export const VWORLD_LAYER_ID = "vworld_satellite";

/** VWorld WMTS Satellite: path uses {z}/{y}/{x} (not standard XYZ order). */
export function vworldSatelliteTileUrl(key: string, z: number, y: number, x: number): string {
  return `https://api.vworld.kr/req/wmts/1.0.0/${encodeURIComponent(key)}/Satellite/${z}/${y}/${x}.jpeg`;
}

export function vworldRasterTiles(key: string): string[] {
  return [
    `https://api.vworld.kr/req/wmts/1.0.0/${encodeURIComponent(key)}/Satellite/{z}/{y}/{x}.jpeg`,
  ];
}

export function vectorAttributionHtml(): string {
  return (
    '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a> © ' +
    '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>'
  );
}

/** MapTiler Free plan: text attribution plus logo when satellite is visible. */
export function satelliteAttributionHtml(options?: { vworld?: boolean }): string {
  const vworld =
    options?.vworld === true
      ? ` ${VWORLD_ATTRIBUTION_TEXT} <a href="https://www.vworld.kr/" target="_blank" rel="noopener">VWorld</a>`
      : "";
  return (
    '© <a href="https://www.maptiler.com/copyright/" target="_blank" rel="noopener">MapTiler</a> © ' +
    '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors ' +
    '<a class="maptiler-logo-link" href="https://www.maptiler.com/" target="_blank" rel="noopener">' +
    '<img class="maptiler-logo" src="https://api.maptiler.com/resources/logo.svg" width="75" height="16" alt="MapTiler" />' +
    "</a>" +
    vworld
  );
}

export const SATELLITE_SOURCE_ID = "maptiler_satellite";
export const SATELLITE_LAYER_ID = "maptiler_satellite";

export function satelliteRasterTiles(key: string): string[] {
  return [`https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=${encodeURIComponent(key)}`];
}

export type BasemapLayerRef = { id: string; type: string; source?: string };

/** Vector tiles and shaded relief from the OpenFreeMap Bright style. */
export const OPENFREEMAP_VECTOR_SOURCE = "openmaptiles";
export const OPENFREEMAP_SHADED_SOURCE = "ne2_shaded";

/** True for raster basemap overlays (MapTiler / VWorld), not OpenFreeMap vector tiles. */
export function isSatelliteRasterLayer(layer: BasemapLayerRef): boolean {
  return layer.id === SATELLITE_LAYER_ID || layer.id === VWORLD_LAYER_ID;
}

/** True for style layers that belong to the OpenFreeMap vector basemap (not app overlays). */
export function isOpenFreeMapBasemapLayer(layer: BasemapLayerRef): boolean {
  if (isSatelliteRasterLayer(layer)) return false;
  if (layer.type === "background") return true;
  const source = layer.source;
  return source === OPENFREEMAP_VECTOR_SOURCE || source === OPENFREEMAP_SHADED_SOURCE;
}

/**
 * Pick where to insert / move the satellite raster: directly above the vector basemap,
 * below every app layer (geojson routes, edit handles, etc.).
 */
export function pickSatelliteLayerBeforeId(layers: readonly BasemapLayerRef[]): string | undefined {
  for (const layer of layers) {
    if (isSatelliteRasterLayer(layer)) continue;
    if (!isOpenFreeMapBasemapLayer(layer)) return layer.id;
  }
  for (const layer of layers) {
    if (layer.type === "background") continue;
    if (isSatelliteRasterLayer(layer)) continue;
    return layer.id;
  }
  return undefined;
}

/**
 * Stack order (bottom → top): vector basemap, MapTiler satellite, VWorld Korea, app layers.
 * Returns indices for tests: maptiler and vworld must sit below the first app layer, vworld above maptiler.
 */
export function satelliteRasterStackIndices(
  layers: readonly BasemapLayerRef[],
): { maptiler: number; vworld: number; firstApp: number } | null {
  const maptiler = layers.findIndex((l) => l.id === SATELLITE_LAYER_ID);
  const vworld = layers.findIndex((l) => l.id === VWORLD_LAYER_ID);
  const beforeId = pickSatelliteLayerBeforeId(layers);
  if (beforeId === undefined) return null;
  const firstApp = layers.findIndex((l) => l.id === beforeId);
  if (maptiler < 0 || vworld < 0 || firstApp < 0) return null;
  return { maptiler, vworld, firstApp };
}

export function buildSatelliteStyle(key: string): StyleSpecification {
  const tiles = satelliteRasterTiles(key);
  return {
    version: 8,
    sources: {
      [SATELLITE_SOURCE_ID]: {
        type: "raster",
        tiles,
        tileSize: 256,
        maxzoom: 20,
        attribution: SATELLITE_ATTRIBUTION_TEXT,
      },
    },
    layers: [{ id: SATELLITE_LAYER_ID, type: "raster", source: SATELLITE_SOURCE_ID }],
  };
}
